/**
 * 音色 / TTS 配音数据契约（TTS 里程碑 · 刀T1）。
 *
 * 归属定案（用户 2026-10-04）：音色绑定「角色资产卡」，音频卡继承；逐句情绪锚点。
 * 来源三合一 + 市场预留：system（云厂商可商用系统音色）/ design（文字造原创新声）/
 * clone（用户上传**有权**样本复刻）/ market（未来"商用素材市场"占位，见技术债 D-81）。
 * 版权红线：不得预置未授权的真人（演员/配音员）声线；见跨会话记忆。
 * 本文件只放类型 + 纯数据 + 纯函数；合成/克隆/设计走 generationService 的 provider（mock 先行）。
 */

export type VoiceSource = 'system' | 'design' | 'clone' | 'market';

/** 授权元数据（为 D-81 素材市场预留）：市场音色将填充 status/price。 */
export interface VoiceLicense {
  /** 是否可商用 */
  commercial: boolean;
  /** free=内置可 free 用；licensed=已授权；premium=付费市场素材 */
  status: 'free' | 'licensed' | 'premium';
  /** 付费素材价（积分），非付费为 undefined */
  price?: number | undefined;
}

export interface VoiceProfile {
  /** 本地稳定 id（角色卡 voice 槽引用它） */
  id: string;
  /** 后端音色名/voice_id（Qwen voice 或 CosyVoice 克隆/设计返回的 voice_id） */
  voiceId: string;
  name: string;
  source: VoiceSource;
  gender: string;
  age: string;
  desc: string;
  /** 语速倍率 0.5–2 */
  rate: number;
  /** 音高半音 -12..12 */
  pitch: number;
  /** 合成模型 */
  model: string;
  license: VoiceLicense;
  /** 试听音频（data-URI / url，mock 可空） */
  previewUrl?: string | undefined;
}

const SYSTEM_MODEL = 'qwen3-tts-instruct-flash';

/** 旧版 24 个可商用系统音色（Qwen3-TTS-Instruct 官方预设）。 */
const SYSTEM_RAW: [string, string, string, string][] = [
  ['Cherry', '女', '青年', '芊悦，阳光积极、亲切自然'],
  ['Serena', '女', '青年', '苏瑶，温柔小姐姐'],
  ['Ethan', '男', '青年', '晨煦，标准普通话带北方口音'],
  ['Chelsie', '女', '青年', '千雪，二次元虚拟女友'],
  ['Momo', '女', '青年', '茉兔，撒娇搞怪'],
  ['Vivian', '女', '青年', '十三，拽拽的可爱小暴躁'],
  ['Moon', '男', '青年', '月白，率性帅气'],
  ['Maia', '女', '青年', '四月，知性与温柔'],
  ['Kai', '男', '青年', '凯，耳朵的 SPA'],
  ['Nofish', '男', '青年', '不吃鱼，设计师音'],
  ['Bella', '女', '青年', '萌宝，小萝莉'],
  ['Stella', '女', '青年', '阿月，甜美活泼少女音'],
  ['Eldric Sage', '男', '老年', '沧明子，沉稳睿智老者'],
  ['Mia', '女', '青年', '乖小妹，温顺乖巧'],
  ['Mochi', '男', '少年', '沙小弥，聪明小大人'],
  ['Bellona', '女', '青年', '燕铮莺，洪亮字正腔圆'],
  ['Vincent', '男', '中年', '田叔，沙哑烟嗓江湖豪情'],
  ['Bunny', '女', '青年', '萌小姬，萌属性爆棚'],
  ['Neil', '男', '中年', '阿闻，专业新闻主持人'],
  ['Elias', '女', '青年', '墨讲师，知识传播者'],
  ['Arthur', '男', '老年', '徐大爷，质朴乡村嗓音'],
  ['Nini', '女', '青年', '邻家妹妹，甜'],
  ['Seren', '女', '青年', '小婉，温和助眠声线'],
  ['Pip', '男', '少年', '顽屁小孩，调皮童真'],
];

export const SYSTEM_VOICES: VoiceProfile[] = SYSTEM_RAW.map(([name, gender, age, desc]) => ({
  id: `sys-${name}`,
  voiceId: name,
  name,
  source: 'system' as VoiceSource,
  gender,
  age,
  desc,
  rate: 1,
  pitch: 0,
  model: SYSTEM_MODEL,
  license: { commercial: true, status: 'free' as const },
}));

export const DEFAULT_VOICE_ID = 'Cherry';

export function defaultVoice(): VoiceProfile {
  return SYSTEM_VOICES.find((v) => v.voiceId === DEFAULT_VOICE_ID) ?? SYSTEM_VOICES[0]!;
}

/** 配音情绪锚点（旧版剧种情绪表并集 + 中性/旁白）。 */
export const EMOTIONS: string[] = [
  '中性', '喜悦', '悲伤', '愤怒', '恐惧', '惊讶', '紧张', '温柔', '冷漠',
  '兴奋', '绝望', '恳求', '疑惑', '恍然大悟', '讽刺', '命令', '深情', '轻蔑', '威严', '谄媚',
];

/** 情绪 → TTS instruct 文本（语气 + 语速提示，对齐竞品"情绪联动语速/喘息"）。 */
const EMOTION_INSTRUCT: Record<string, string> = {
  中性: '自然平稳地叙述',
  喜悦: '语气明亮上扬，语速稍快，带着笑意',
  悲伤: '语调低沉缓慢，带哽咽与气声',
  愤怒: '语气激烈有力，音量提高，语速偏快',
  恐惧: '声音发颤，气息急促，略带喘息',
  惊讶: '语调骤然上扬，短促而明亮',
  紧张: '语速加快，气息紧绷，略有停顿',
  温柔: '轻柔舒缓，温暖亲切',
  冷漠: '平淡疏离，语气克制，不带起伏',
  兴奋: '语调高扬，节奏明快，情绪饱满',
  绝望: '低沉无力，语速缓慢，气息衰减',
  恳求: '语气柔软而急切，带祈求感',
  疑惑: '语调上扬带疑问，节奏迟疑',
  恍然大悟: '先缓后扬，带醒悟的顿挫',
  讽刺: '拖音、阴阳顿挫，带不屑',
  命令: '干脆有力，不容置疑',
  深情: '温暖绵长，含情脉脉',
  轻蔑: '冷淡上扬，带不屑一顾',
  威严: '低沉厚重，不怒自威',
  谄媚: '音调甜腻讨好，语速偏快',
};

export function emotionInstruction(emotion: string): string {
  const e = emotion.trim();
  if (e === '' || e === '中性') {
    return EMOTION_INSTRUCT['中性']!;
  }
  return EMOTION_INSTRUCT[e] ?? `以${e}的情绪表达`;
}

/**
 * 解析某音频卡最终用哪条音色（体现 A 决策：音色绑角色、音频卡继承、可临时覆盖）。
 * 优先级：音频卡显式覆盖 > 所属角色绑定音色 > 默认系统音色。
 */
export function resolveAudioVoice(input: {
  override?: VoiceProfile | undefined;
  roleVoice?: VoiceProfile | undefined;
}): VoiceProfile {
  return input.override ?? input.roleVoice ?? defaultVoice();
}

/** 造一条自定义音色（声音设计/复刻返回）。默认标记不可商用风险由调用方按来源设定。 */
export function makeCustomVoice(input: {
  source: Extract<VoiceSource, 'design' | 'clone' | 'market'>;
  voiceId: string;
  name: string;
  desc?: string;
  model?: string;
  license?: VoiceLicense;
  previewUrl?: string;
}): VoiceProfile {
  return {
    id: `${input.source}-${input.voiceId}`,
    voiceId: input.voiceId,
    name: input.name,
    source: input.source,
    gender: '中性',
    age: '青年',
    desc: input.desc ?? '',
    rate: 1,
    pitch: 0,
    model: input.model ?? 'cosyvoice-v3.5-plus',
    license: input.license ?? { commercial: input.source === 'market', status: input.source === 'market' ? 'premium' : 'licensed' },
    previewUrl: input.previewUrl,
  };
}
